// The blog index lists. Converted from https://www.dbos.dev/blog.

import { useSyncExternalStore } from 'react';
import { Img } from '@components/cms/Img';
import { cx } from '@components/webflow/element';
import { featuredPosts, latestPosts } from '@content/blog';
import { longDate } from '@lib/format';

/** The large card at the top of /blog: the post marked `featured`. */
export function FeaturedPosts() {
  return (
    <div className="card-insight-feature-wrapper w-dyn-list">
      <div role="list" className="w-dyn-items">
        {featuredPosts.slice(0, 1).map((post, i) => (
          <div role="listitem" className="collection-item-2 w-dyn-item" key={i}>
            <a className="card-insight-feature-link w-inline-block" href={`/blog/${post.slug}`}>
              <div className="card-insight-feature-image">
                <Img image={post.cardImage} loading="lazy" width="Auto" className="image-6" />
                <div className="card-insight-feature-overlay" />
              </div>
              <div className="card-insight-feature-content">
                <h3 className="text-heading-h2 text-heading-featured">{post.title}</h3>
                <p className="text-body text-alignment-left clamp-text">{post.summary}</p>
                <div className="card-insight-meta">
                  <div className="text-caption-pill w-dyn-bind-empty" />
                  <div className="text-caption-micro">{longDate(post.date)}</div>
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_SIZE = 6;
/** Webflow's pagination query parameter, kept so existing links keep working. */
const PARAM = '58edfe63_page';

const noSubscription = () => () => {};
const requestedPage = () => Math.max(1, Number(new URLSearchParams(window.location.search).get(PARAM)) || 1);

/** "Latest articles": every non-featured post, newest first, six per page. */
export function LatestArticles() {
  // Prerendered as page 1 (static hosting can't vary by query string); after hydration the
  // page requested in the URL is shown.
  const page = useSyncExternalStore(noSubscription, requestedPage, () => 1);
  const pageCount = Math.ceil(latestPosts.length / PAGE_SIZE);
  const items = latestPosts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  return (
    <div className="card-insight-collection-wrapper w-dyn-list">
      <div role="list" className="card-insight-collection w-dyn-items">
        {items.map((post, i) => (
          <div role="listitem" className="card-insight-collection-item w-dyn-item" key={i}>
            <a className="card-insight w-inline-block" href={`/blog/${post.slug}`}>
              <Img image={post.thumbnail} loading="lazy" className="card-insight-image" />
              <div className="card-insight-content">
                <h3 className="text-heading-h4">{post.title}</h3>
                <div className="card-insight-meta">
                  <div className={cx('tagline-container text-size-small', !post.category && 'w-dyn-bind-empty')}>
                    {post.category}
                  </div>
                  <div className="text-caption-micro">{longDate(post.date)}</div>
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>
      <div role="navigation" aria-label="List" className="w-pagination-wrapper pagination-2">
        {page > 1 && (
          <a
            aria-label="Previous Page"
            className="w-pagination-previous slider-button-left"
            href={`?${PARAM}=${page - 1}`}
          >
            <div className="button-pagination-arrow w-embed">
              <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                {'\n'}
                <path
                  d="M7.72266 13.1787L1.24958 6.99986L7.72266 0.821018"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {'\n'}
                <path
                  d="M1.25 7L14.75 7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {'\n'}
              </svg>
            </div>
          </a>
        )}
        {page < pageCount && (
          <a aria-label="Next Page" className="w-pagination-next slider-button-right" href={`?${PARAM}=${page + 1}`}>
            <div className="slider-arrow-right w-embed">
              <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                {'\n'}
                <path
                  d="M7.82617 0.821289L14.2992 7.0001L7.82617 13.1789"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {'\n'}
                <path
                  d="M14.2991 7L1.69922 7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {'\n'}
              </svg>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}
